// Reusable hero for SEO landing pages
import { ArrowRight, Star, MapPin } from "lucide-react";
import { Button } from "./ui/button";
import { LOGO_URL } from "../mock";

export default function PageHero({
  kicker,
  h1,
  h1Accent,
  marker,
  description,
  city = "Mississauga",
  bgImage,
  primaryCta = { label: "Book Now", href: "#contact" },
  secondaryCta,
}) {
  return (
    <section className="relative min-h-[92vh] flex items-center overflow-hidden bg-jester-deep pt-20">
      <div className="absolute inset-0">
        <img
          src={bgImage}
          alt=""
          className="w-full h-full object-cover scale-105 opacity-45"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#120127] via-[#120127]/85 to-[#120127]/30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#120127]/30 via-transparent to-[#120127]" />
      </div>

      <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#8D39EE]/15 blur-[140px]" />
      <div className="absolute top-1/4 -left-20 w-[400px] h-[400px] rounded-full bg-[#3C1478]/40 blur-[120px]" />

      <img
        src={LOGO_URL}
        alt=""
        aria-hidden="true"
        className="hidden lg:block absolute right-[6%] top-1/2 -translate-y-1/2 w-[380px] opacity-85 drop-shadow-[0_0_60px_rgba(141,57,238,0.6)] spin-slow"
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 w-full pt-16 pb-24">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#A8E000]/40 bg-[#A8E000]/8 backdrop-blur-sm mb-8 fade-up">
            <MapPin className="w-3.5 h-3.5 text-acid" />
            <span className="text-xs font-label uppercase tracking-[0.3em] text-acid">
              {kicker}
            </span>
          </div>

          <h1
            className="font-display text-[11vw] md:text-[5.5rem] leading-[0.92] tracking-tight fade-up"
            style={{ animationDelay: "100ms" }}
          >
            {h1}
            {h1Accent && (
              <>
                <br />
                <span className="text-jester">{h1Accent}</span>
              </>
            )}
            {marker && (
              <>
                <br />
                <span className="font-marker text-acid text-[7vw] md:text-[2.8rem] inline-block mt-2">
                  {marker}
                </span>
              </>
            )}
          </h1>

          <p
            className="mt-8 text-lg md:text-xl text-white/80 max-w-2xl leading-relaxed fade-up"
            style={{ animationDelay: "250ms" }}
          >
            {description}
          </p>

          <div
            className="mt-10 flex flex-wrap items-center gap-4 fade-up"
            style={{ animationDelay: "380ms" }}
          >
            <Button
              asChild
              size="lg"
              className="bg-acid hover:bg-[#bff020] text-[#120127] font-label tracking-[0.2em] rounded-full px-8 h-13 shadow-[0_0_40px_rgba(168,224,0,0.45)]"
            >
              <a href={primaryCta.href} className="flex items-center gap-2">
                {primaryCta.label} <ArrowRight className="w-4 h-4" />
              </a>
            </Button>
            {secondaryCta && (
              <Button
                asChild
                variant="outline"
                size="lg"
                className="rounded-full border-jester bg-jester-purple/15 hover:bg-jester-purple/30 text-white h-13 px-8 font-label tracking-[0.2em]"
              >
                <a href={secondaryCta.href}>{secondaryCta.label}</a>
              </Button>
            )}
          </div>

          <div className="mt-12 flex items-center gap-4 fade-up" style={{ animationDelay: "520ms" }}>
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4" style={{ color: "#FDC814", fill: "#FDC814" }} />
              ))}
            </div>
            <span className="text-sm text-white/70 font-label tracking-[0.15em]">
              5.0 · 47+ Google reviews · {city}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
